import axios from 'axios';

// Configure API base URL
const BASE_URL = process.env.REACT_APP_API_URL || 'https://savedelete.com';
const API_BASE_URL = `${BASE_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false, // Don't include cookies to avoid CORS issues
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Article interfaces
/** One link of the previous / next chain inside a category (blog/adjacent.py). */
export interface AdjacentArticle {
  title: string;
  slug: string;
  /** /article/<slug>/, or a link post's external_url. */
  url: string;
}

export interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  author: string;
  author_name: string;
  author_username?: string;
  category: string;
  category_name: string;
  tags: Tag[];
  featured_image: string;
  featured_image_srcset?: string;
  featured_image_sizes?: string;
  featured_image_width?: number;
  featured_image_height?: number;
  /** JPEG/PNG for og:image and twitter:image (featured_image is WebP). Detail endpoint only. */
  og_image?: string | null;
  status: string;
  published_at: string;
  views_count: number;
  is_featured: boolean;
  /** Paid placement. Editorial surfaces filter on ?is_sponsored=false. */
  is_sponsored?: boolean;
  reading_time: number;
  created_at?: string;
  updated_at?: string;
  meta_title?: string;
  meta_description?: string;
  featured_image_caption?: string;
  author_bio?: string;
  author_avatar?: string;
  author_article_count?: number;
  author_twitter?: string;
  author_linkedin?: string;
  takeaways?: string[] | null;
  series?: SeriesData | null;
  /** Previous / next editorial article in the same category, oldest to newest. */
  adjacent_articles?: { previous: AdjacentArticle | null; next: AdjacentArticle | null };
  series_order?: number | null;
  allow_comments?: boolean;
  comments_count?: number;
  schema_jsonld?: string | null;
  display_updated_at?: string | null;
}

export interface SeriesMember {
  id: string;
  title: string;
  slug: string;
  series_order: number | null;
}

export interface SeriesData {
  id: string;
  title: string;
  slug: string;
  description?: string;
  cover_image?: string | null;
  articles: SeriesMember[];
}

export interface Tag {
  id: string;
  name: string;
  slug: string;
  articles_count: number;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  articles_count?: number;
}

// Author interface
export interface Author {
  id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  bio?: string;
  profile_image?: string;
  website?: string;
  twitter_handle?: string;
  linkedin_url?: string;
  articles_count?: number;
  date_joined?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Contact form interfaces
export interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  website?: string;          // honeypot — must stay empty; bots fill it
  turnstile_token?: string;
}

// Newsletter interfaces
export interface NewsletterSubscription {
  email: string;
}

// API service functions
export const articleService = {
  // Get all articles with pagination
  getArticles: async (page = 1, pageSize = 20, filters?: any) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      ...filters,
    });
    const response = await api.get<PaginatedResponse<Article>>(`/articles/?${params}`);
    return response.data;
  },

  // Get single article by slug
  getArticle: async (slug: string) => {
    const response = await api.get<any>(`/articles/${slug}/`);
    const data = response.data;

    // Transform nested data to flat structure expected by Article interface
    // Get full name or fallback to username
    const authorFullName = data.author?.first_name || data.author?.last_name
      ? `${data.author?.first_name || ''} ${data.author?.last_name || ''}`.trim()
      : data.author?.username || 'Anonymous';

    return {
      ...data,
      author: data.author?.id || data.author,
      author_name: authorFullName || data.author_name || 'Anonymous',
      author_username: data.author?.username || data.author_username,
      category: data.category?.slug || data.category,
      category_name: data.category?.name || data.category_name || 'Uncategorized',
      author_bio: data.author?.bio || data.author_bio,
      author_avatar: data.author?.profile_image || data.author_avatar,
      author_article_count: data.author?.articles_count || data.author_article_count,
      author_twitter: data.author?.twitter_handle || data.author_twitter,
      author_linkedin: data.author?.linkedin_url || data.author_linkedin,
    } as Article;
  },

  // Report one reader view (when is decided in src/lib/articleView.ts).
  // Fire-and-forget. No credentials, so a signed-in reader's session never
  // meets a CSRF check; keepalive, so a view reported as the tab closes lands.
  recordView: (slug: string): void => {
    fetch(`${API_BASE_URL}/articles/${encodeURIComponent(slug)}/view/`, {
      method: 'POST', credentials: 'omit', keepalive: true,
    }).catch(() => { /* a lost view is not worth surfacing */ });
  },

  // Get featured articles
  getFeaturedArticles: async () => {
    const response = await api.get<Article[]>('/articles/featured/');
    return response.data;
  },

  // Get trending articles
  getTrendingArticles: async () => {
    const response = await api.get<Article[]>('/articles/trending/');
    return response.data;
  },

  // Get recent articles
  getRecentArticles: async () => {
    const response = await api.get<Article[]>('/articles/recent/');
    return response.data;
  },

  // Search articles
  searchArticles: async (query: string, page = 1) => {
    const params = new URLSearchParams({
      search: query,
      page: page.toString(),
    });
    const response = await api.get<PaginatedResponse<Article>>(`/articles/?${params}`);
    return response.data;
  },

  // Like an article
  likeArticle: async (slug: string) => {
    const response = await api.post(`/articles/${slug}/like/`);
    return response.data;
  },

  // Share an article
  shareArticle: async (slug: string) => {
    const response = await api.post(`/articles/${slug}/share/`);
    return response.data;
  },

  // Get related articles
  getRelatedArticles: async (slug: string) => {
    const response = await api.get<Article[]>(`/articles/${slug}/related/`);
    return response.data;
  },
};

export const categoryService = {
  // Get all categories — single fetch with a generous page_size.
  //
  // The previous implementation paginated through `?page=1`, `?page=2`, ...
  // until `next === null`, issuing 5 sequential network requests for ~50
  // categories. Mobile perf audit on 2026-05-09 measured ~2.5s of API
  // time across the chain, blocking every page that uses Navigation /
  // HeroSection / GuidesHeader (which is virtually every page).
  //
  // page_size=200 fetches the entire category set in one go. The Django
  // ViewSet honors page_size; SafePageNumberPagination caps it sensibly.
  // The list endpoint also now ships with edge-cache hints (5 min browser,
  // 1 hour edge), so subsequent navigations skip the network entirely.
  getCategories: async () => {
    try {
      const response = await api.get<PaginatedResponse<Category>>('/categories/?page_size=200');
      const results = response.data?.results || [];
      return {
        count: results.length,
        next: null,
        previous: null,
        results,
      };
    } catch (error) {
      console.error('Error fetching categories:', error);
      return {
        count: 0,
        next: null,
        previous: null,
        results: []
      };
    }
  },

  // Get category by slug
  getCategory: async (slug: string) => {
    const response = await api.get<Category>(`/categories/${slug}/`);
    return response.data;
  },

  // Get articles by category
  getCategoryArticles: async (slug: string, page = 1, pageSize = 12) => {
    const response = await api.get<PaginatedResponse<Article>>(`/categories/${slug}/articles/?page=${page}&page_size=${pageSize}`);
    return response.data;
  },
};

export const tagService = {
  // Get all tags
  getTags: async () => {
    const response = await api.get<PaginatedResponse<Tag>>('/tags/');
    return response.data;
  },

  // Get tag by slug
  getTag: async (slug: string) => {
    const response = await api.get<Tag>(`/tags/${slug}/`);
    return response.data;
  },

  // Get articles by tag
  getTagArticles: async (slug: string, page = 1, pageSize = 12) => {
    const response = await api.get<PaginatedResponse<Article>>(`/tags/${slug}/articles/?page=${page}&page_size=${pageSize}`);
    return response.data;
  },
};

// Page interface
export interface Page {
  id: string;
  title: string;
  slug: string;
  page_type: string;
  content: string;
  excerpt?: string;
  featured_image?: string;
  featured_image_alt?: string;
  meta_title?: string;
  meta_description?: string;
}

export const pageService = {
  // Get page by type
  getPageByType: async (pageType: string) => {
    const response = await api.get<Page>(`/pages/${pageType}/`);
    return response.data;
  },
};

export const contactService = {
  // Send contact form
  sendContactForm: async (contactData: ContactFormData) => {
    const response = await api.post('/contact/', contactData);
    return response.data;
  },
};

export const newsletterService = {
  // Subscribe to newsletter. The Turnstile token is required in production
  // (the backend bypasses verification only when TURNSTILE_SECRET_KEY is unset
  // for local dev). The honeypot field must remain empty for real users —
  // bots that fill it get a silent 200 from the backend without DB write.
  subscribe: async (
    email: string,
    turnstileToken: string,
    website: string = '',
    // Cadence the subscriber picked. Defaults to the model default so any
    // caller that omits it keeps its current behaviour.
    frequency: 'daily' | 'weekly' = 'daily',
  ) => {
    const response = await api.post('/newsletter/', {
      email,
      turnstile_token: turnstileToken,
      website,
      frequency,
    });
    return response.data;
  },

  // Unsubscribe from newsletter (if endpoint exists)
  unsubscribe: async (email: string) => {
    const response = await api.delete('/newsletter/', { data: { email } });
    return response.data;
  },
};

export const authorService = {
  // Get author by username
  getAuthor: async (username: string) => {
    const response = await api.get<Author>(`/users/${username}/`);
    return response.data;
  },

  // Get author's articles by username
  getAuthorArticles: async (username: string, page = 1, pageSize = 12) => {
    // Use author_username parameter to filter by username
    const response = await api.get<PaginatedResponse<Article>>(`/articles/?author_username=${username}&page=${page}&page_size=${pageSize}`);
    return response.data;
  },
};

// Auth service for admin/editor check
export const authService = {
  checkAdminStatus: async () => {
    try {
      const response = await api.get('/admin-status/');
      return response.data;
    } catch (error) {
      // If error, assume not admin
      return {
        is_authenticated: false,
        is_admin: false,
        can_edit: false
      };
    }
  }
};

export { api as apiClient };
export default api;